/**
 * Scroll reveal for editorial sections.
 *
 * Progressive enhancement contract:
 *  - The hiding CSS is gated behind `html.lux-reveal-ready`, which is set by a
 *    synchronous snippet in <head> only when IntersectionObserver exists and the
 *    visitor has not asked for reduced motion. If this script never runs, or the
 *    browser is old, or motion is reduced, every element stays visible.
 *  - Elements are revealed once and then unobserved — no scroll listeners.
 */

(() => {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Two hooks: the attribute for markup authored here, and the class for theme
  // blocks (group / image) whose only extension point is a class string.
  const REVEAL_ATTR = '[data-lux-reveal], .lux-reveal';
  const STAGGER_MS = 90;
  const MAX_STAGGER_STEPS = 5;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    {
      // Fire slightly before the element reaches the viewport so the motion
      // finishes as it settles into view rather than starting late.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.01,
    }
  );

  /**
   * Observe a subtree, staggering siblings that share a parent so groups of
   * cards cascade instead of all landing at once.
   * @param {ParentNode} root
   */
  function observeWithin(root) {
    const targets = root.querySelectorAll(REVEAL_ATTR);
    /** @type {Map<Element, number>} */
    const seenPerParent = new Map();

    for (const el of targets) {
      if (el.classList.contains('is-revealed')) continue;

      // Anything already in view on load reveals immediately — no fade-in on
      // content the visitor has not scrolled to.
      const parent = el.parentElement;
      if (parent) {
        const index = seenPerParent.get(parent) ?? 0;
        seenPerParent.set(parent, index + 1);
        if (index > 0) {
          const step = Math.min(index, MAX_STAGGER_STEPS);
          el.style.setProperty('--lux-reveal-delay', `${step * STAGGER_MS}ms`);
        }
      }

      observer.observe(el);
    }
  }

  function init() {
    observeWithin(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor re-renders sections in place; pick up any new targets.
  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target) observeWithin(target);
  });
})();
